import { useEffect, useState } from 'react';
import { api, fmt } from '../api.ts';
import type { Card } from './JobCardPrint.tsx';
import { LayersEditor, PlyPicker, type FaceBoard } from './Layers.tsx';
import {
  ONE_PLY, draftsOf, fluteOfBoxType, layersBody, layersComplete, layersFor, type LayerDraft,
} from '../lib/layers.ts';

/*
 * The corrugation card on its own (067), from the list it is pending on.
 *
 * Only what corrugation needs -- the ply and its layers (085) -- so nothing
 * else on the card is in the way. A layer starts at the running size and the
 * sheets, so usually only GSM and BF are typed. Open until Corrugation starts.
 */

export default function CorrugationCardDialog({ jobId, onClose, onDone }: {
  jobId: number; onClose: () => void; onDone: (msg: string) => void;
}) {
  const [card, setCard] = useState<Card | null>(null);
  const [ply, setPly] = useState<number | null>(null);
  const [kraft, setKraft] = useState(false);
  const [rows, setRows] = useState<LayerDraft[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const start = (c: Card) => ({
    size: c.running_size ?? '',
    qty: String(Number(c.boards[0]?.sheets ?? c.board_sheet_count ?? '') || ''),
    flute: fluteOfBoxType(c.box_type),
  });

  useEffect(() => {
    api.get<{ card: Card }>(`/api/jobs/${jobId}/card`)
      .then(({ card: c }) => {
        setCard(c);
        const p = c.box_ply ?? c.ply;
        setPly(p);
        setKraft(Boolean(c.kraft_printing));
        setRows(layersFor(p, Boolean(c.kraft_printing), draftsOf(c.liners), start(c)));
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not open the card'));
  }, [jobId]);

  const relist = (p: number | null, k: boolean) => {
    setPly(p); setKraft(k);
    if (card) setRows((r) => layersFor(p, k, r, start(card)));
  };
  const complete = layersComplete(ply, kraft, rows);
  const face: FaceBoard | null = card?.boards[0]
    ? { name: card.boards[0].name, size: `${card.boards[0].lengthCm} X ${card.boards[0].widthCm} CM`,
        sheets: card.boards[0].sheets }
    : null;

  async function save() {
    if (!card || !complete) return;
    setBusy(true); setError('');
    try {
      await api.patch(`/api/jobs/${jobId}/card`, {
        ...(!card.box_ply && ply !== card.ply ? { ply } : {}),
        kraftPrinting: kraft,
      });
      await api.put(`/api/jobs/${jobId}/liners`, { liners: layersBody(ply, kraft, rows) });
      onDone(`${card.job_number} corrugation card complete ✓`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the corrugation card');
    } finally { setBusy(false); }
  }

  return (
    <div className="backdrop" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ width: 940, maxWidth: 'calc(100vw - 32px)' }} onClick={(e) => e.stopPropagation()}
           onKeyDown={(e) => {
             if (e.key === 'Enter' && e.target instanceof HTMLInputElement) void save();
           }}>
        <div className="modal-head">
          <h3>Corrugation card · {card?.job_number ?? '…'}</h3>
          {card && (
            <p>
              {card.item_name}{card.box_size ? ` · ${card.box_size}` : ''}
              {card.boards[0] ? ` · ${fmt(String(card.boards[0].sheets))} sheets` : ''}
            </p>
          )}
        </div>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          {!card ? <div className="dim">Loading…</div> : (
            <>
              <PlyPicker ply={ply} boxPly={card.box_ply} kraftPrinting={kraft} make={card.make}
                         onPly={(p) => relist(p, kraft)} onKraft={(k) => relist(ply, k)} />
              {ply === null ? (
                <div className="hint">Choose the ply to list its layers.</div>
              ) : ply === 1 ? (
                <div className="hint ly-missing">{ONE_PLY}</div>
              ) : (
                <LayersEditor rows={rows} kraftPrinting={kraft} board={face} onChange={setRows} />
              )}
            </>
          )}
        </div>
        <div className="modal-foot">
          {card && !complete && (
            <span className="hint" style={{ marginRight: 'auto' }}>
              {ply === null || ply < 3 ? 'Choose 3, 5 or 7 ply' : 'Give every layer its GSM and BF'}
            </span>
          )}
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={() => void save()} disabled={busy || !complete}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
